import { Snackbar } from "@dropbox/dig-components/snackbar";
import { UIIcon } from "@dropbox/dig-icons";
import { FailLine } from "@dropbox/dig-icons/assets";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { useAssistTemplatesContext } from "@mirage/mosaics/ComposeAssistant/data/AssistTemplatesContext";
import type { UserMessageOptions } from "@mirage/service-conversation/types";
import type { AssistCustomTemplate } from "@mirage/shared/compose/assist-template";
import { SnackbarView } from "@mirage/shared/snackbar/SnackbarView";
import { useCallback, useState } from "react";

interface UseAIToolLibraryTemplatesProps {
  onTemplateChange: (templateId: string | null, templateName: string) => void;
  onCloseLibrary: () => void;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  setEmptyChatOption: (option: ClickableEmptyChatInstructionsOption) => void;
  currentTemplateID?: string | null;
  onPostMessageWithTemplate: (templateId: string) => void;
  markdownDraft: string;
}

interface UseAIToolLibraryTemplatesReturn {
  templates: AssistCustomTemplate[];
  selectedTemplateId?: string;
  handleTemplateChange: (
    templateId: string | null,
    templateName: string,
  ) => void;
  handleUseTemplate: (templateID: string, templateName: string) => void;
  saveTemplate: (
    template: AssistCustomTemplate,
    onSaved?: (savedTemplate: AssistCustomTemplate) => void,
  ) => Promise<void>;
  deleteTemplate: (templateId: string) => void;
  createdTemplateSnackbarMsg: string | undefined;
  handleSetCreatedTemplateSnackbarMsg: (msg: string | undefined) => void;
  templateSettingsSnackbar: React.ReactNode;
}

export const useAIToolLibraryTemplates = ({
  onTemplateChange,
  onCloseLibrary,
  setUserMessageOptions,
  setEmptyChatOption,
  currentTemplateID,
  onPostMessageWithTemplate,
  markdownDraft,
}: UseAIToolLibraryTemplatesProps): UseAIToolLibraryTemplatesReturn => {
  const {
    templates = [],
    saveTemplate: baseTemplateSave,
    deleteTemplate: baseTemplateDelete,
  } = useAssistTemplatesContext();

  const [createdTemplateSnackbarMsg, setCreatedTemplateSnackbarMsg] = useState<
    undefined | string
  >();

  const handleSetCreatedTemplateSnackbarMsg = useCallback(
    (msg: string | undefined) => {
      setCreatedTemplateSnackbarMsg(msg);
    },
    [],
  );

  const handleTemplateChange = useCallback(
    (templateId: string | null, templateName: string) => {
      onTemplateChange(templateId, templateName);
    },
    [onTemplateChange],
  );

  const handleUseTemplate = useCallback(
    (templateID: string, templateName: string) => {
      if (markdownDraft?.length > 0) {
        onPostMessageWithTemplate(templateID);
      } else {
        setEmptyChatOption("write");
        setUserMessageOptions({ agentIds: ["write"] });
        handleTemplateChange(templateID, templateName);
      }
      onCloseLibrary();
    },
    [
      handleTemplateChange,
      onCloseLibrary,
      setUserMessageOptions,
      setEmptyChatOption,
      onPostMessageWithTemplate,
      markdownDraft,
    ],
  );

  const saveTemplate = useCallback(
    async (
      template: AssistCustomTemplate,
      onSaved?: (savedTemplate: AssistCustomTemplate) => void,
    ) => {
      await baseTemplateSave(template);
      onSaved?.(template);
    },
    [baseTemplateSave],
  );

  const deleteTemplate = useCallback(
    (templateId: string) => {
      baseTemplateDelete(templateId);
    },
    [baseTemplateDelete],
  );

  const templateSettingsSnackbar = createdTemplateSnackbarMsg && (
    <Snackbar.Position>
      <SnackbarView
        title={createdTemplateSnackbarMsg}
        onClose={() => setCreatedTemplateSnackbarMsg(undefined)}
        accessory={<UIIcon src={FailLine} />}
        timeoutMs={3000}
      />
    </Snackbar.Position>
  );

  return {
    templates,
    selectedTemplateId: currentTemplateID || undefined,
    handleTemplateChange,
    handleUseTemplate,
    saveTemplate,
    deleteTemplate,
    createdTemplateSnackbarMsg,
    handleSetCreatedTemplateSnackbarMsg,
    templateSettingsSnackbar,
  };
};
